import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createExercise,
  getExercise,
  getExerciseEquipment,
  getExerciseMedia,
  setExerciseArchived,
  setExerciseEquipment,
  updateExercise,
} from '../features/exercises/api';
import { listEquipment } from '../features/equipment/api';
import type { EquipmentRow, ExerciseMediaRow, ExerciseRow } from '../lib/types';
import {
  EXERCISE_KINDS,
  LOAD_MODES,
  MUSCLE_GROUPS,
  SPORTS,
  label,
  type ExerciseKind,
  type LoadMode,
  type Sport,
} from '../lib/vocab';
import { defaultTracksForKind } from '../lib/tracks';
import { deriveEquipmentClass } from '../lib/equipmentClass';
import { validateIncrementKg, validateName } from '../lib/validators';
import { friendlyErrorMessage } from '../lib/errors';
import { useToast } from '../components/Toast';
import { Button } from '../components/Button';
import { SelectField, TextAreaField, TextField } from '../components/Field';
import { Chip, ChipRow } from '../components/Chip';
import { Spinner } from '../components/Spinner';
import { EquipmentPicker } from '../features/exercises/EquipmentPicker';
import { HowToEditor } from '../features/exercises/HowToEditor';
import { DemoPhotoEditor, ExtraImagesEditor, VideosEditor, exercisePreview } from '../features/exercises/MediaEditors';
import { PreviewModal } from '../features/preview/PreviewModal';
import type { ExerciseInfo } from '../features/preview/subject';
import { mediaUrl } from '../lib/storage';

export function ExerciseEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(!isNew);
  const [row, setRow] = useState<ExerciseRow | null>(null);
  const [allEquipment, setAllEquipment] = useState<EquipmentRow[]>([]);
  const [media, setMedia] = useState<ExerciseMediaRow[]>([]);
  const [equipmentIds, setEquipmentIds] = useState<string[]>([]);

  const [nameEn, setNameEn] = useState('');
  const [nameHe, setNameHe] = useState('');
  const [sport, setSport] = useState<Sport>('strength');
  const [muscleGroup, setMuscleGroup] = useState('chest');
  const [muscles, setMuscles] = useState<string[]>([]);
  const [kind, setKind] = useState<ExerciseKind>('weight_reps');
  const [loadMode, setLoadMode] = useState<LoadMode>('external');
  const [tracks, setTracks] = useState(defaultTracksForKind('weight_reps'));
  const [incrementKg, setIncrementKg] = useState(2.5);
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionHe, setDescriptionHe] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    listEquipment().then(setAllEquipment);
    if (!isNew && id) {
      Promise.all([getExercise(id), getExerciseEquipment(id), getExerciseMedia(id)]).then(([ex, links, mediaRows]) => {
        if (ex) {
          setRow(ex);
          setNameEn(ex.name_en);
          setNameHe(ex.name_he);
          setSport(ex.sport);
          setMuscleGroup(ex.muscle_group);
          setMuscles(ex.muscles ?? []);
          setKind(ex.kind);
          setLoadMode(ex.load_mode);
          setTracks({
            track_reps: ex.track_reps,
            track_weight: ex.track_weight,
            track_time: ex.track_time,
            track_distance: ex.track_distance,
            track_rir: ex.track_rir,
          });
          setIncrementKg(ex.increment_kg);
          setDescriptionEn(ex.description_en ?? '');
          setDescriptionHe(ex.description_he ?? '');
        }
        // getExerciseEquipment already orders by sort ascending.
        setEquipmentIds(links.map((l) => l.equipment_id));
        setMedia(mediaRows);
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function handleKindChange(nextKind: ExerciseKind) {
    setKind(nextKind);
    setTracks(defaultTracksForKind(nextKind));
  }

  function refreshMedia() {
    if (id && !isNew) getExerciseMedia(id).then(setMedia);
  }

  async function handleSave() {
    const nextErrors: Record<string, string | null> = {
      nameEn: validateName(nameEn, 'English name', 120),
      nameHe: validateName(nameHe, 'Hebrew name', 120),
      incrementKg: validateIncrementKg(incrementKg),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const equipmentClass = deriveEquipmentClass(
      equipmentIds.map((eid, sort) => ({ equipment_id: eid, class: allEquipment.find((e) => e.id === eid)?.class ?? 'other', sort })),
      loadMode,
    );

    const input = {
      name_en: nameEn,
      name_he: nameHe,
      muscle_group: muscleGroup,
      equipment: equipmentClass,
      kind,
      increment_kg: incrementKg,
      sport,
      load_mode: loadMode,
      ...tracks,
      description_en: descriptionEn || null,
      description_he: descriptionHe || null,
      muscles,
    };

    setSaving(true);
    try {
      if (isNew) {
        const newId = await createExercise(input);
        if (equipmentIds.length > 0) await setExerciseEquipment(newId, equipmentIds);
        toast.show('Exercise created', 'success');
        navigate(`/exercises/${newId}`);
      } else if (id) {
        await updateExercise(id, input);
        await setExerciseEquipment(id, equipmentIds);
        toast.show('Saved', 'success');
      }
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleArchive() {
    if (!row) return;
    try {
      await setExerciseArchived(row.id, !row.archived_at);
      setRow({ ...row, archived_at: row.archived_at ? null : new Date().toISOString() });
      toast.show(row.archived_at ? 'Unarchived' : 'Archived', 'success');
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  function toggleMuscle(m: string) {
    setMuscles((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  if (loading) return <Spinner />;

  const previewInfo: ExerciseInfo = {
    name: nameEn,
    muscleGroup,
    muscles,
    sport,
    description: descriptionEn || null,
    equipment: equipmentIds.map((eid) => {
      const item = allEquipment.find((e) => e.id === eid);
      return { id: eid, name: item?.name_en ?? eid, photoUrl: mediaUrl(item?.photo_path) };
    }),
  };

  return (
    <div>
      <div className="topbar">
        <h1 className="page-title">{isNew ? 'New exercise' : nameEn}</h1>
        {!isNew && row ? (
          <Button tone="neutral" onClick={handleToggleArchive} style={{ flex: 'initial' }}>
            {row.archived_at ? 'Unarchive' : 'Archive'}
          </Button>
        ) : null}
      </div>

      <div className="card" style={{ maxWidth: 760, marginBottom: 20 }}>
        <div className="row">
          <TextField label="Name (English)" value={nameEn} onChange={(e) => setNameEn(e.target.value)} error={errors.nameEn} />
          <TextField label="Name (Hebrew)" dirAuto value={nameHe} onChange={(e) => setNameHe(e.target.value)} error={errors.nameHe} />
        </div>
        <div className="row">
          <SelectField label="Sport" value={sport} onChange={(e) => setSport(e.target.value as Sport)} options={SPORTS.map((s) => ({ value: s, label: label(s) }))} />
          <SelectField
            label="Muscle group"
            value={muscleGroup}
            onChange={(e) => setMuscleGroup(e.target.value)}
            options={MUSCLE_GROUPS.map((m) => ({ value: m, label: label(m) }))}
          />
        </div>
        <div className="field">
          <label>Secondary muscles</label>
          <ChipRow>
            {MUSCLE_GROUPS.map((m) => (
              <Chip key={m} active={muscles.includes(m)} onClick={() => toggleMuscle(m)}>
                {label(m)}
              </Chip>
            ))}
          </ChipRow>
        </div>
        <div className="row">
          <SelectField label="Kind" value={kind} onChange={(e) => handleKindChange(e.target.value as ExerciseKind)} options={EXERCISE_KINDS.map((k) => ({ value: k, label: label(k) }))} />
          <SelectField label="Load mode" value={loadMode} onChange={(e) => setLoadMode(e.target.value as LoadMode)} options={LOAD_MODES.map((l) => ({ value: l, label: label(l) }))} />
        </div>
        <div className="field">
          <label>Tracks</label>
          <ChipRow>
            {(['track_reps', 'track_weight', 'track_time', 'track_distance', 'track_rir'] as const).map((t) => (
              <Chip key={t} active={tracks[t]} onClick={() => setTracks((prev) => ({ ...prev, [t]: !prev[t] }))}>
                {t.replace('track_', '')}
              </Chip>
            ))}
          </ChipRow>
        </div>
        <TextField
          label="Increment (kg)"
          type="number"
          step="0.5"
          value={incrementKg}
          onChange={(e) => setIncrementKg(Number(e.target.value))}
          error={errors.incrementKg}
          style={{ maxWidth: 160 }}
        />
        <TextAreaField label="Description (English)" value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} />
        <TextAreaField label="Description (Hebrew)" dirAuto value={descriptionHe} onChange={(e) => setDescriptionHe(e.target.value)} />

        <div className="field">
          <label>Equipment</label>
          <EquipmentPicker allEquipment={allEquipment} selectedIds={equipmentIds} onChange={setEquipmentIds} />
        </div>

        <Button onClick={handleSave} disabled={saving} style={{ width: '100%' }}>
          {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
        </Button>
      </div>

      {!isNew && id ? <HowToEditor exerciseId={id} /> : null}

      {!isNew && id ? (
        <div className="card" style={{ maxWidth: 760 }}>
          <div className="topbar" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 22 }}>Media</h2>
            <Button tone="tonal" size="sm" onClick={() => setPreviewing(true)} style={{ flex: 'initial' }}>
              Preview
            </Button>
          </div>
          <DemoPhotoEditor exerciseId={id} media={media} info={previewInfo} onChanged={refreshMedia} />
          <VideosEditor exerciseId={id} media={media} info={previewInfo} onChanged={refreshMedia} />
          <ExtraImagesEditor exerciseId={id} media={media} info={previewInfo} onChanged={refreshMedia} />
          {previewing ? <PreviewModal subject={exercisePreview(previewInfo, media)} onClose={() => setPreviewing(false)} /> : null}
        </div>
      ) : (
        <p className="muted">Save the exercise first to add photos and videos.</p>
      )}
    </div>
  );
}
